import { AppShell as MantineAppShell, Menu, UnstyledButton } from "@mantine/core";
import { useCallback, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import { Icon } from "@/components/ui/Icon";
import { avatarColorFor, initialsOf } from "@/lib/dynamic/avatar";
import { logout } from "@/lib/auth";
import { useUserStore } from "@/store/user-store";
import { roleLabel } from "@/lib/dynamic/roleCodes";

/**
 * AdminAppShell — 시스템 관리자(공급사 / 슈퍼관리자) 전용 콘솔 셸.
 *
 * 회사 AppShell(문서관리·CAPA·… 사이드바)과 **시각적으로 명확히 구분**됨:
 * - 사이드바/헤더에 어드민 악센트(.eqms-admin — 딥 인디고 배경) 적용.
 * - 브랜드 = "시스템 관리자 콘솔 · 공급사" 배지 명시.
 * - 회사 업무 모듈 nav 없음. 어드민 nav = 모듈 프로비저닝 / 고객사 관리 / 전역 표준·시드 / 플랫폼 설정.
 * - 하단에 "회사 앱으로 돌아가기" 링크(운영 앱 복귀).
 *
 * 배포(00-menu-structure §43-95): prod = admin.eqms.co.kr 별도 서브도메인.
 * 개발/폴백 = /admin/* 경로(localhost 서브도메인 불가 대응). 라우팅은 routes/index.tsx 에서 분기.
 *
 * 접근제어: 슈퍼관리자(isSuperAdmin)만 진입. 가드는 라우트(AdminGuard)가 담당 — 신뢰 경계는 서버.
 * 셸 마크업/클래스는 globals.css 목업 CSS(.eqms-shell/.eqms-sidebar/.topbar/.nav-item …) 재사용.
 */

/** 어드민 콘솔 nav 항목 (경로는 /admin/* 폴백 기준 상대 경로) */
interface AdminNavItem {
  /** 상대 경로 (AdminRoutes 하위) */
  to: string;
  /** i18n 키 */
  i18nKey: string;
  /** Icon 이름 */
  icon: string;
}

const ADMIN_NAV: AdminNavItem[] = [
  {
    to: "provisioning",
    i18nKey: "admin.nav.provisioning",
    icon: "layers",
  },
  { to: "companies", i18nKey: "admin.nav.workspaces", icon: "user" },
  { to: "codes", i18nKey: "admin.nav.codes", icon: "list" },
  {
    to: "workflows",
    i18nKey: "admin.nav.workflows",
    icon: "settings",
  },
  {
    to: "security-policy",
    i18nKey: "admin.nav.securityPolicy",
    icon: "lock",
  },
  { to: "audit-log", i18nKey: "admin.nav.auditLog", icon: "history" },
];

export function AdminAppShell() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const user = useUserStore((s) => s.user);
  const clearUser = useUserStore((s) => s.clearUser);

  const userName = user?.name ?? t("user.guest", "게스트");
  const userRole =
    roleLabel(user?.roleCode, user?.roleName) || t("admin.roleSuper", "공급사 슈퍼관리자");

  const onLogout = useCallback(async () => {
    clearUser();
    await logout();
    navigate("/login", { replace: true });
  }, [clearUser, navigate]);

  return (
    <MantineAppShell
      padding={0}
      styles={{
        root: { "--app-shell-padding": "0px" } as CSSProperties,
        main: { backgroundColor: "var(--bg)", minHeight: "100vh", padding: 0, paddingTop: 0 },
      }}
    >
      <MantineAppShell.Main>
        {/* .eqms-admin 클래스로 어드민 악센트(딥 인디고 사이드바/헤더) 적용 — 회사 셸과 시각 구분 */}
        <div className="eqms-shell eqms-admin">
          {/* ===== 어드민 사이드바 ===== */}
          <nav className="eqms-sidebar">
            {/* 브랜드 — "시스템 관리자 콘솔 · 공급사" 명시 */}
            <div className="sb-brand adm-brand">
              <span className="adm-brand-nm">{t("admin.consoleTitle", "시스템 관리자 콘솔")}</span>
              <span className="adm-brand-badge">{t("admin.supplierBadge", "공급사")}</span>
            </div>

            <div className="sb-scroll">
              <div className="sb-group-label">{t("admin.nav.group", "플랫폼 운영")}</div>
              {ADMIN_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
                >
                  <Icon name={item.icon} size={18} />
                  <span>{t(item.i18nKey)}</span>
                </NavLink>
              ))}
            </div>
          </nav>

          {/* ===== 메인(헤더 + 콘텐츠) ===== */}
          <main className="eqms-main">
            <header className="topbar adm-topbar">
              <div className="adm-topbar-title">
                <Icon name="shieldCheck" size={16} />
                <span>{t("admin.headerTitle", "시스템 관리자 콘솔 · 공급사")}</span>
              </div>

              <div className="tb-spacer" />

              {/* 어드민 모드 배지 — 회사 앱과 구분되는 시각 신호 */}
              <div className="adm-mode-pill">
                <span className="dot" />
                {t("admin.modePill", "SUPER ADMIN")}
              </div>

              {/* 사용자 메뉴 (로그아웃) */}
              <Menu shadow="md" width={220} position="bottom-end">
                <Menu.Target>
                  <UnstyledButton className="tb-user" aria-label={t("user.menu", "사용자 메뉴")}>
                    <span
                      className="avatar"
                      style={{ background: avatarColorFor(user?.sub ?? user?.email, userName) }}
                    >
                      {initialsOf(userName)}
                    </span>
                    <div>
                      <div className="nm">{userName}</div>
                      {userRole && <div className="rl">{userRole}</div>}
                    </div>
                    <svg
                      className="chev"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>{user?.email ?? ""}</Menu.Label>
                  <Menu.Item onClick={onLogout}>{t("user.logout", "로그아웃")}</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </header>

            {/* 여백 래퍼 .page 는 각 페이지 루트가 가짐(이중 패딩 방지) */}
            <div className="content">
              <Outlet />
            </div>
          </main>
        </div>
      </MantineAppShell.Main>
    </MantineAppShell>
  );
}
